import { useLayoutEffect, useRef } from 'react'

const FALLBACK_PAGINATION_TOOLBAR_HEIGHT = 56
const MIN_TABLE_HEIGHT = 200

export const TABLE_CONTAINER_MAX_HEIGHT_VAR = '--mrt-table-container-max-height'

const registerAsNonInherited = (propertyName: string) => {
  if (typeof CSS === 'undefined' || !('registerProperty' in CSS)) return
  try {
    CSS.registerProperty({ name: propertyName, syntax: '*', inherits: false })
  } catch (error) {
    if (!(error instanceof DOMException)) throw error
  }
}

const getAvailableTableHeight = (
  distanceFromViewportTop: number,
  paginationToolbarHeight: number,
  viewportHeight: number,
) => Math.max(MIN_TABLE_HEIGHT, viewportHeight - distanceFromViewportTop - paginationToolbarHeight)

/**
 * Bounds the table container to the space left in the viewport so the pagination bar below it stays
 * visible. Re-measures whenever the body or anything above the table resizes (the bulk-action bar
 * appears when rows are selected, the toolbar grows when the search field opens or filters wrap).
 * Pass a `remeasureKey` that changes on such layout shifts.
 */
export const useMeasuredTableContainerHeight = (remeasureKey: unknown) => {
  const ref = useRef<HTMLDivElement>(null)
  const topContentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    registerAsNonInherited(TABLE_CONTAINER_MAX_HEIGHT_VAR)

    const updateMaxHeight = () => {
      const distanceFromViewportTop = element.getBoundingClientRect().top
      const paginationToolbarHeight =
        element.nextElementSibling?.getBoundingClientRect().height ?? FALLBACK_PAGINATION_TOOLBAR_HEIGHT
      const maxHeight = getAvailableTableHeight(distanceFromViewportTop, paginationToolbarHeight, window.innerHeight)
      element.style.setProperty(TABLE_CONTAINER_MAX_HEIGHT_VAR, `${maxHeight}px`)
    }

    updateMaxHeight()
    const resizeObserver = new ResizeObserver(updateMaxHeight)
    resizeObserver.observe(document.body)
    for (let toolbar = element.previousElementSibling; toolbar; toolbar = toolbar.previousElementSibling) {
      resizeObserver.observe(toolbar)
    }
    if (topContentRef.current) resizeObserver.observe(topContentRef.current)
    window.addEventListener('resize', updateMaxHeight)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateMaxHeight)
    }
  }, [remeasureKey])

  return { ref, topContentRef }
}
